import type { CaseFilter } from "@/queries/useFetchCaseFiltersets";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { SlidersHorizontal } from "lucide-react";
import type React from "react";
import { useMemo, useState } from "react";

const SavedFiltersSelect = styled(FormControl)(({ theme }) => ({
    minWidth: 200,
    margin: theme.spacing(0, 2),
}));

const SearchBarContainer = styled(Box)({
    display: "flex",
    alignItems: "center",
    gap: "16px",
    width: "100%",
});

const FilterSearchInput = styled(TextField)(({ theme }) => ({
    "& .MuiInputBase-root": {
        fontSize: "0.875rem",
        padding: theme.spacing(0.5, 1),
    },
}));

interface SearchBarProps {
    onAdvancedFilters: () => void;
    savedFilters: CaseFilter[];
    onFilterSelect: (filter: CaseFilter | null) => void;
    selectedFilter: CaseFilter | null;
    children?: React.ReactNode;
}

const SearchBar: React.FC<SearchBarProps> = ({
    onAdvancedFilters,
    savedFilters,
    onFilterSelect,
    selectedFilter,
    children,
}) => {
    const [filterSearchTerm, setFilterSearchTerm] = useState("");

    const handleFilterChange = (event: SelectChangeEvent) => {
        const filterId = event.target.value as string;
        const filter = savedFilters.find((f) => f.id === filterId);
        onFilterSelect(filter || null);
    };

    const filteredSavedFilters = useMemo(() => {
        if (!filterSearchTerm) {
            return savedFilters;
        }

        return savedFilters.filter((filter) =>
            filter.name?.toLowerCase().includes(filterSearchTerm.toLowerCase()),
        );
    }, [savedFilters, filterSearchTerm]);

    return (
        <SearchBarContainer>
            <SavedFiltersSelect size="small">
                <InputLabel id="saved-filters-label" sx={{ top: "6px" }}>
                    Saved Filters
                </InputLabel>
                <Select
                    labelId="saved-filters-label"
                    value={selectedFilter?.id || ""}
                    onChange={handleFilterChange}
                    label="Saved Filters"
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                maxHeight: 300,
                            },
                        },
                    }}
                >
                    <Box sx={{ p: 1 }}>
                        <FilterSearchInput
                            size="small"
                            fullWidth
                            placeholder="Search filters..."
                            value={filterSearchTerm}
                            onChange={(e) => setFilterSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </Box>
                    <MenuItem value="">None</MenuItem>
                    {filteredSavedFilters.map((filter) => (
                        <MenuItem key={filter.id} value={filter.id}>
                            {filter.name}
                        </MenuItem>
                    ))}
                </Select>
            </SavedFiltersSelect>
            <IconButton size="small" onClick={onAdvancedFilters} sx={{ p: "4px" }}>
                <SlidersHorizontal size={18} />
            </IconButton>
            {children}
        </SearchBarContainer>
    );
};

export default SearchBar;
